import classNames from 'classnames'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import type { ViewProps } from '../common-props'
import type { ReactElement } from 'react'
import type { IBuilderConfig, IDataBuilderConfig } from '~/types/graph-builder'

import BuilderForm, { getDefaultConfig } from './BuilderForm'

import ContentLayout from '~/components/ContentLayout'
import DynamicDataBlock from '~/components/DynamicDataBlock'
import DynamicGraphBlock from '~/components/DynamicGraphBlock'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import { GRAPH_TYPE } from '~/const/graphs-builder'
import { useAnalyticsContext } from '~/providers/Analytics'
import { useAppSummary } from '~/providers/AppSummary'
import { useGetChartConfigQuery } from '~/queries/graphs-builder'
import { isBusinessReportsFeature } from '~/utils/analytics-context'

import './styles.scss'

const Builder = ({ eventsData, isAppLoading, kpiField }: ViewProps): ReactElement => {
  /** Local state */

  const { id: configId } = useParams() as { id?: string }

  const { dispatch, state } = useAnalyticsContext()

  const { appKey, deviceType } = useAppSummary()

  const isBusinessReports = isBusinessReportsFeature(state.feature)

  const [config, setConfig] = useState(getDefaultConfig(isBusinessReports))

  const classes = classNames({ 'filters-collapsed': state.isFiltersCollapsed })

  /** Queries & Mutations */

  const { data: configData, isLoading: isConfigDataLoading } = useGetChartConfigQuery(configId)

  /** Handlers */

  const handleConfigChange = useCallback((newConfig: IBuilderConfig) => {
    setConfig(newConfig)
  }, [])

  useEffect(() => {
    if (configData && configData.feature_name) {
      if (isBusinessReportsFeature(configData.feature_name)) {
        dispatch({ payload: 'business_reports', type: 'SET_FEATURE' })
      }
    }
  }, [configData, dispatch])

  return (
    <Layout className="dynamic-graph-layout" withMargin>
      <ContentLayout loading={isAppLoading}>
        <div className="builder-container">
          {isConfigDataLoading ? (
            <div className="builder-form-component">
              <MwMLoader category="item" />
            </div>
          ) : config.series && config.series.length > 0 ? (
            <>
              {config.series.length === 1 && config.series[0].graphType === GRAPH_TYPE.RAW ? (
                <DynamicDataBlock
                  appKey={appKey}
                  className={classes}
                  config={config as IDataBuilderConfig}
                  deviceType={deviceType}
                  isAppLoading={isAppLoading}
                  kpiField={kpiField}
                />
              ) : null}
              {config.series.every((el) => el.graphType !== GRAPH_TYPE.RAW) ? (
                <DynamicGraphBlock
                  appKey={appKey}
                  className={classes}
                  config={config}
                  deviceType={deviceType}
                  eventsData={eventsData}
                  isAppLoading={isAppLoading}
                  kpiField={isBusinessReports ? 'date' : kpiField}
                />
              ) : null}
            </>
          ) : null}
          {isConfigDataLoading ? (
            <div className="builder-form-component">
              <MwMLoader category="item" />
            </div>
          ) : (
            <BuilderForm config={configData} onConfigChange={handleConfigChange} />
          )}
        </div>
      </ContentLayout>
    </Layout>
  )
}

export default Builder
